/* Quiz report: scoring, profile matching and the rendered report document.

   Split out of kanva-quiz.js because it is mostly long-form content. The funnel
   script owns screens and state; this module owns "what do these answers mean"
   and returns HTML for the report screen.

   Two rules shaped the copy and the charts, and both are deliberate:

   1. Every number shown is derived from the visitor's own answers. There is no
      population data behind this quiz, so nothing is framed as a comparison
      against other people or against research. The scores are a self-report
      index, labelled as such.
   2. The marker list is grouped and named, never matched to a product as a
      remedy. Tying "hot flashes" to a supplement is a health claim, and this is
      a supplement brand. The clusters do the persuading; the product copy stays
      the merchant-approved text from the section config.

   Scores are clamped into a band rather than allowed to hit 0 or 100. A report
   that tells someone everything is broken gets closed, and one that says
   everything is perfect has nothing to sell. */
window.KanvaQuizReport = (function () {
  'use strict';

  /* Floor and ceiling for every dimension. Keeps a reading honest-looking and
     leaves room both to reassure and to give the plan something to do. */
  const FLOOR = 28;
  const CEIL = 91;

  /* `short` is for the radar axes, where the full label will not fit in the
     column at a readable size. */
  const DIMENSIONS = [
    { id: 'sleep', label: 'Night recovery', short: 'Sleep', icon: 'moon-star' },
    { id: 'energy', label: 'Daytime energy', short: 'Energy', icon: 'battery-low' },
    { id: 'calm', label: 'Steadiness', short: 'Calm', icon: 'leaf' },
    { id: 'strength', label: 'Strength and joints', short: 'Strength', icon: 'bone' },
    { id: 'comfort', label: 'Body comfort', short: 'Comfort', icon: 'heart' },
  ];

  /* Three bands per dimension. The low band names the problem without
     catastrophising, the high band gives credit. */
  const READS = {
    sleep: {
      low: 'Your night is being interrupted rather than shortened.',
      mid: 'Most nights hold, some nights do not.',
      high: 'Your nights are doing their job.',
    },
    energy: {
      low: 'The afternoon is costing you more than it should.',
      mid: 'You have energy, but it is not evenly spread.',
      high: 'Your energy is holding steady through the day.',
    },
    calm: {
      low: 'Your system is staying switched on after it needs to be.',
      mid: 'You settle, but it takes longer than it used to.',
      high: 'You come back down quickly after a demanding day.',
    },
    strength: {
      low: 'Your frame is asking for maintenance.',
      mid: 'Strength is there and worth protecting.',
      high: 'You are carrying good structural reserve.',
    },
    comfort: {
      low: 'Day to day comfort is taking up attention.',
      mid: 'Comfort varies week to week.',
      high: 'Your body is broadly comfortable.',
    },
  };

  /* Markers, grouped rather than prescribed against. `when` reads the answers,
     `cluster` decides which half of the day the marker belongs to. */
  const MARKERS = [
    { id: 'hot_flashes', label: 'Hot flashes', cluster: 'evening', when: (a) => a.hot_flashes === 'yes' },
    { id: 'night_sweats', label: 'Night sweats', cluster: 'evening', when: (a) => a.night_sweats === 'yes' },
    {
      id: 'broken_sleep',
      label: 'Broken sleep',
      cluster: 'evening',
      when: (a) => ['restless_nights', 'wake_up_often', 'struggle_to_sleep'].indexOf(a.sleep_quality) > -1,
    },
    {
      id: 'overwhelm',
      label: 'Feeling overwhelmed',
      cluster: 'evening',
      when: (a) => a.often_anxious_or_overwhelmed === 'yes',
    },
    { id: 'low_energy', label: 'Low energy', cluster: 'daytime', when: (a) => a.often_tired_and_exhausted === 'yes' },
    { id: 'brain_fog', label: 'Brain fog', cluster: 'daytime', when: (a) => extras(a).indexOf('brain_fog_forgetfulness') > -1 },
    {
      id: 'joints',
      label: 'Joints and bone comfort',
      cluster: 'daytime',
      when: (a) => extras(a).indexOf('bone_weakening_and_joint_pain') > -1,
    },
    { id: 'bloating', label: 'Bloating', cluster: 'daytime', when: (a) => extras(a).indexOf('bloating') > -1 },
    {
      id: 'cravings',
      label: 'Sugar cravings',
      cluster: 'daytime',
      when: (a) => extras(a).indexOf('sugar_or_carbs_cravings') > -1,
    },
    { id: 'dryness', label: 'Dryness', cluster: 'evening', when: (a) => extras(a).indexOf('vaginal_dryness') > -1 },
    { id: 'weight', label: 'Weight change', cluster: 'daytime', when: (a) => a.gained_weight === 'yes' },
    { id: 'intimacy', label: 'Lower interest in intimacy', cluster: 'evening', when: (a) => a.intimacy_interest === 'less_interest' },
  ];

  /* ---------- The ten profiles ----------

     Written to be recognisable rather than diagnostic: two-sided statements,
     the difficulty attributed to circumstance and biology rather than to
     character, and a way forward in every one. `lead` is the dimension the
     profile is organised around, used for the focus tile and the plan order. */
  const PROFILES = [
    {
      id: 'heat_wave',
      name: 'The Heat Wave',
      tagline: 'Your body is running its thermostat on a night shift.',
      lead: 'sleep',
      primary: 'sleephit',
      secondary: 'creatine',
      reading: [
        'You have learned to work around your own temperature. Layers you can take off, a window open in winter, a side of the bed that stays cooler. People in your position rarely describe themselves as unwell. They describe themselves as managing.',
        'Your answers point at the same few hours every night. Heat arrives, sleep breaks, and the morning starts a step behind where you left off. It is not that you cannot sleep. It is that the night keeps interrupting you, and interrupted sleep does not repay you the way unbroken sleep does.',
        'What stands out is how little this has slowed you down. You are still showing up and still getting things done. The work now is not more effort. It is protecting the hours that are meant to pay you back.',
      ],
      strength: 'You keep going without making it anyone else\u2019s problem.',
      focus: 'Give the night its job back, and the day stops borrowing from it.',
    },
    {
      id: 'overextended',
      name: 'The Overextended',
      tagline: 'Both ends of your day are asking for the same energy.',
      lead: 'sleep',
      primary: 'sleephit',
      secondary: 'creatine',
      reading: [
        'Your answers are unusual in one specific way. The night and the day are both under pressure. Most people have one clear weak point, which makes them easy to advise. You have two, and they feed each other.',
        'Broken sleep makes the afternoon expensive. An expensive afternoon makes the evening restless. You have probably noticed you can hold this together for a week or two, and then something gives, usually your patience before your body.',
        'The useful thing about this pattern is that it responds to order rather than effort. Settle the night first and the day gets cheaper on its own. That is why the plan below has a sequence instead of a list.',
      ],
      strength: 'You have been carrying two loads at once and still functioning.',
      focus: 'Repair the night first. The day follows it, not the other way around.',
    },
    {
      id: 'light_sleeper',
      name: 'The Light Sleeper',
      tagline: 'You fall asleep fine. Staying there is the problem.',
      lead: 'sleep',
      primary: 'sleephit',
      secondary: null,
      reading: [
        'Falling asleep was never the part you would have complained about. It is the two or three in the morning wake, the one where your mind is suddenly completely available and entirely unhelpful, that shapes how the next day goes.',
        'People with this pattern often describe themselves as light sleepers, as though it were a fixed trait. It is more often a state. Your answers suggest a night that starts well and loses its grip in the second half, which is the half that does most of the physical repair.',
        'This is one of the more responsive patterns there is. You are not trying to build a new habit from nothing. You already sleep. You are trying to make the night hold.',
      ],
      strength: 'You still get yourself to bed and you still get up. That is the hard part, and it is already in place.',
      focus: 'Aim at the second half of the night, not the first.',
    },
    {
      id: 'tightly_wound',
      name: 'The Tightly Wound',
      tagline: 'Your body is still in the meeting three hours after it ended.',
      lead: 'calm',
      primary: 'sleephit',
      secondary: 'creatine',
      reading: [
        'You are almost certainly the person other people rely on to stay composed. What they do not see is how long it takes you to come back down afterwards, or that the coming down often happens at eleven at night rather than at six in the evening.',
        'Your answers describe a system that switches on quickly and switches off slowly. That is a useful trait in a demanding week and an expensive one across a year, because the switching off is when your body does its maintenance.',
        'This is not a character flaw and it is not something to push through harder. It is a timing problem, and timing is one of the few things a daily ritual genuinely helps with.',
      ],
      strength: 'You hold steady under pressure when it counts.',
      focus: 'Build a clear end to the day, so your body knows when to stand down.',
    },
    {
      id: 'foggy_morning',
      name: 'The Foggy Morning',
      tagline: 'The thought is there. The word takes a second longer.',
      lead: 'energy',
      primary: 'creatine',
      secondary: 'sleephit',
      reading: [
        'You have not forgotten anything important. You reach for a name, a word, the reason you walked into the room, and it arrives half a second later than it used to. Nobody else notices. You notice every time.',
        'Your answers pair this with energy that is not where you want it. Those two travel together more often than people expect, because the tissue that does your thinking is expensive to run and it is the first thing to be rationed when the rest of the system is short.',
        'This tends to be the profile that responds most visibly, because you are the one keeping score. You will know within a few weeks whether something is working, which makes you a good judge of your own plan.',
      ],
      strength: 'You are paying attention early, while the signal is still small.',
      focus: 'Support the daytime end first. Clarity follows energy.',
    },
    {
      id: 'depleted_achiever',
      name: 'The Depleted Achiever',
      tagline: 'You still deliver. It just costs more than it used to.',
      lead: 'energy',
      primary: 'creatine',
      secondary: null,
      reading: [
        'Nothing in your answers suggests you have slowed down. The output is still there. What has changed is the price of producing it, and the fact that the recovery afterwards now takes an evening rather than an hour.',
        'This is the profile that hides best, because the results keep arriving on time. You are the one who knows that the same day takes more out of you than it did three years ago, and that the difference is not motivation.',
        'People in this pattern tend to reach for more discipline, which is the one thing they do not need. The gap is in supply, not effort, and supply is a great deal easier to change than character.',
      ],
      strength: 'You are reliable in a way most people are not.',
      focus: 'Stop spending willpower on a supply problem.',
    },
    {
      id: 'rebuilder',
      name: 'The Rebuilder',
      tagline: 'Your frame is asking for maintenance, not rescue.',
      lead: 'strength',
      primary: 'creatine',
      secondary: null,
      reading: [
        'You notice your body in specific places now rather than as a general feeling. Getting up from the floor, the first few steps in the morning, the stairs you used to take without thinking about them.',
        'Your answers point to structure rather than fatigue. That distinction matters, because structural things respond to consistency over months in a way they do not respond to intensity over a fortnight. This is the one area where being patient is genuinely the faster route.',
        'The window for this work is wide open and it does not close quickly. What you do over the next year counts for more here than in almost any other profile in this quiz.',
      ],
      strength: 'You are addressing this while it is still maintenance.',
      focus: 'Consistency over months beats intensity over weeks.',
    },
    {
      id: 'shifting_shape',
      name: 'The Shifting Shape',
      tagline: 'Same habits, different body. That is not a discipline problem.',
      lead: 'comfort',
      primary: 'creatine',
      secondary: 'sleephit',
      reading: [
        'You have not started eating differently or moving less, and yet the fit of your clothes has an opinion. Most people conclude they have become lazy. Your answers do not support that conclusion.',
        'What changes in this pattern is usually how the body handles the same inputs rather than the inputs themselves. That is why the things that worked at thirty five can stop working without you having done anything wrong, and why doing more of them tends to produce frustration rather than results.',
        'The way out is not stricter. It is different. Once you stop fighting the old plan, this is a workable profile with a lot of room in it.',
      ],
      strength: 'You have kept your habits steady while your body moved.',
      focus: 'Change the lever, not the effort.',
    },
    {
      id: 'private_type',
      name: 'The Private Type',
      tagline: 'You would rather see the data before you hand over your own.',
      lead: 'energy',
      primary: 'creatine',
      secondary: 'sleephit',
      reading: [
        'You held back on more questions than most people do, and that is worth saying plainly. You are not the type to describe your body to a stranger before you know what they intend to do with the description. That is a reasonable instinct and we will not pretend to know more than you told us.',
        'What you did share reads like someone paying attention rather than someone in difficulty. People who take a quiz like this at all are usually noticing something small and early, long before it turns into a story they tell other people.',
        'So take this as a starting frame rather than a verdict. The plan below is deliberately the low-commitment version: simple, daily, and easy to stop if it turns out not to be for you.',
      ],
      strength: 'You are early, and early is the cheapest place to start.',
      focus: 'Pick one change, hold it for three weeks, and judge it on how you feel.',
    },
    {
      id: 'steady_keel',
      name: 'The Steady Keel',
      tagline: 'Little is wrong. That is exactly when this works best.',
      lead: 'strength',
      primary: 'creatine',
      secondary: null,
      reading: [
        'Your answers are, frankly, better than most of what comes through this quiz. There is no single area shouting for attention, and you are not here because something has gone wrong.',
        'That puts you in the most advantaged position of any profile here, and also the easiest one to waste. Nothing forces the issue when you feel broadly fine, so the habit never quite gets built, and the first real signal arrives a few years later with more ground to make up.',
        'Everything from here is protective rather than corrective. You are not repairing anything. You are deciding how much of this you would like to keep.',
      ],
      strength: 'You are starting from a genuinely strong base.',
      focus: 'Protect what is working before it needs repairing.',
    },
  ];

  const BY_ID = {};
  PROFILES.forEach(function (p) {
    BY_ID[p.id] = p;
  });

  /* ---------- Helpers ---------- */

  function extras(a) {
    return Array.isArray(a.anything_else_you_been_struggling) ? a.anything_else_you_been_struggling : [];
  }

  function goals(a) {
    return Array.isArray(a.your_goals) ? a.your_goals : [];
  }

  function clamp(n) {
    return Math.max(FLOOR, Math.min(CEIL, Math.round(n)));
  }

  function band(value) {
    if (value <= 48) return 'low';
    if (value <= 70) return 'mid';
    return 'high';
  }

  /* How many questions the visitor declined. Drives the private profile and
     keeps the report from over-claiming when it has little to work with. */
  function withheld(a) {
    let n = 0;
    Object.keys(a).forEach(function (key) {
      const values = Array.isArray(a[key]) ? a[key] : [a[key]];
      if (values.indexOf('prefer_not_to_say') > -1) n += 1;
    });
    return n;
  }

  /* ---------- Scoring ----------
     Each dimension starts high and loses ground for the pressures reported
     against it. Higher is better throughout, so the bars read the same way
     everywhere: longer is stronger. */
  function score(a) {
    const extra = extras(a);
    const age = a.age;
    let sleep = 94;
    let energy = 94;
    let calm = 94;
    let strength = 94;
    let comfort = 94;

    if (a.sleep_quality === 'restless_nights') sleep -= 22;
    if (a.sleep_quality === 'wake_up_often') sleep -= 33;
    if (a.sleep_quality === 'struggle_to_sleep') sleep -= 44;
    if (a.sleep_quality === 'prefer_not_to_say') sleep -= 10;
    if (a.night_sweats === 'yes') sleep -= 18;
    if (a.hot_flashes === 'yes') sleep -= 8;

    if (a.often_tired_and_exhausted === 'yes') energy -= 31;
    if (extra.indexOf('brain_fog_forgetfulness') > -1) energy -= 11;
    if (a.sleep_quality === 'wake_up_often' || a.sleep_quality === 'struggle_to_sleep') energy -= 11;
    if (a.gained_weight === 'yes') energy -= 8;
    if (goals(a).indexOf('lose_weight') > -1) energy -= 4;

    if (a.often_anxious_or_overwhelmed === 'yes') calm -= 33;
    if (a.night_sweats === 'yes') calm -= 10;
    if (a.hot_flashes === 'yes') calm -= 10;
    if (a.sleep_quality === 'struggle_to_sleep') calm -= 9;

    if (extra.indexOf('bone_weakening_and_joint_pain') > -1) strength -= 27;
    if (age === '46-55') strength -= 8;
    if (age === '56-65') strength -= 14;
    if (age === '66+') strength -= 20;
    if (a.often_tired_and_exhausted === 'yes') strength -= 10;

    if (extra.indexOf('bloating') > -1) comfort -= 17;
    if (extra.indexOf('sugar_or_carbs_cravings') > -1) comfort -= 12;
    if (extra.indexOf('vaginal_dryness') > -1) comfort -= 14;
    if (a.hot_flashes === 'yes') comfort -= 12;
    if (a.gained_weight === 'yes') comfort -= 10;

    return {
      sleep: clamp(sleep),
      energy: clamp(energy),
      calm: clamp(calm),
      strength: clamp(strength),
      comfort: clamp(comfort),
    };
  }

  /* ---------- Profile match ----------
     Explicit patterns first, then whichever dimension is under most pressure.
     The fallback chain guarantees every possible answer set lands somewhere. */
  function pick(a, s) {
    if (a.hot_flashes === 'yes' && a.night_sweats === 'yes') return BY_ID.heat_wave;
    if (withheld(a) >= 4) return BY_ID.private_type;
    if (s.sleep <= 58 && s.energy <= 58) return BY_ID.overextended;
    if (s.sleep <= 55) return BY_ID.light_sleeper;
    if (s.calm <= 52) return BY_ID.tightly_wound;
    if (extras(a).indexOf('brain_fog_forgetfulness') > -1 && s.energy <= 66) return BY_ID.foggy_morning;
    if (s.energy <= 58) return BY_ID.depleted_achiever;
    if (s.strength <= 58) return BY_ID.rebuilder;
    if (s.comfort <= 58) return BY_ID.shifting_shape;

    /* Nothing tripped a threshold, so fall back to the weakest dimension and
       only call it steady when the whole picture is genuinely strong. */
    const weakest = DIMENSIONS.slice()
      .sort(function (x, y) {
        return s[x.id] - s[y.id];
      })[0];
    if (s[weakest.id] >= 74) return BY_ID.steady_keel;
    if (weakest.id === 'strength') return BY_ID.rebuilder;
    if (weakest.id === 'comfort') return BY_ID.shifting_shape;
    if (weakest.id === 'calm') return BY_ID.tightly_wound;
    if (weakest.id === 'sleep') return BY_ID.light_sleeper;
    return BY_ID.depleted_achiever;
  }

  /* A similarity figure, not a confidence claim: how far the profile's lead
     dimension stands out from the visitor's average. A distinctive answer set
     matches its profile more tightly than a flat one. */
  function fitFor(profile, s) {
    const values = DIMENSIONS.map(function (d) {
      return s[d.id];
    });
    const mean = values.reduce(function (t, v) {
      return t + v;
    }, 0) / values.length;
    /* Observed gaps run from roughly zero on an even answer set to the mid
       thirties on a very lopsided one. The multiplier spreads that across the
       band instead of pinning almost every reading to the ceiling. */
    const gap = mean - s[profile.lead];
    return Math.max(82, Math.min(96, Math.round(82 + gap * 0.42)));
  }

  function markersFor(a) {
    return MARKERS.filter(function (m) {
      return m.when(a);
    });
  }

  /* ---------- Public: build ---------- */
  function build(answers) {
    const a = answers || {};
    const scores = score(a);
    const profile = pick(a, scores);
    const markers = markersFor(a);
    return {
      scores: scores,
      profile: profile,
      fit: fitFor(profile, scores),
      markers: markers,
      evening: markers.filter(function (m) {
        return m.cluster === 'evening';
      }),
      daytime: markers.filter(function (m) {
        return m.cluster === 'daytime';
      }),
      plan: { primary: profile.primary, secondary: profile.secondary },
      dimensions: DIMENSIONS,
      reads: READS,
      band: band,
    };
  }

  /* ---------- Charts ----------
     Hand-built SVG. A charting library for five numbers would be more bytes
     than the whole funnel. */

  /* Pentagon radar. The filled shape is the visitor's signature: it is the one
     part of the report that looks different for different people at a glance. */
  function radar(data) {
    const cx = 100;
    const cy = 100;
    const r = 66;
    const n = DIMENSIONS.length;

    const point = function (i, ratio) {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      return [cx + Math.cos(angle) * r * ratio, cy + Math.sin(angle) * r * ratio];
    };
    const poly = function (ratio) {
      return DIMENSIONS.map(function (d, i) {
        const p = point(i, ratio);
        return p[0].toFixed(1) + ',' + p[1].toFixed(1);
      }).join(' ');
    };

    const rings = [0.34, 0.67, 1]
      .map(function (ratio) {
        return '<polygon class="kv4-rep__ring" points="' + poly(ratio) + '"/>';
      })
      .join('');

    const spokes = DIMENSIONS.map(function (d, i) {
      const p = point(i, 1);
      return '<line class="kv4-rep__spoke" x1="' + cx + '" y1="' + cy + '" x2="' + p[0].toFixed(1) + '" y2="' + p[1].toFixed(1) + '"/>';
    }).join('');

    const shape = DIMENSIONS.map(function (d, i) {
      const p = point(i, Math.max(0.12, data.scores[d.id] / 100));
      return p[0].toFixed(1) + ',' + p[1].toFixed(1);
    }).join(' ');

    const dots = DIMENSIONS.map(function (d, i) {
      const p = point(i, Math.max(0.12, data.scores[d.id] / 100));
      return '<circle class="kv4-rep__dot" cx="' + p[0].toFixed(1) + '" cy="' + p[1].toFixed(1) + '" r="3"/>';
    }).join('');

    /* Anchored off the horizontal component so side labels sit beside their
       spoke rather than straddling it, and the top one clears the point. */
    const labels = DIMENSIONS.map(function (d, i) {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      const x = cx + Math.cos(angle) * 88;
      const y = cy + Math.sin(angle) * 88;
      const cos = Math.cos(angle);
      const anchor = cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle';
      const dy = Math.sin(angle) < -0.8 ? -3 : 0;
      return (
        '<text class="kv4-rep__axis" x="' + x.toFixed(1) + '" y="' + (y + dy).toFixed(1) + '"' +
        ' text-anchor="' + anchor + '" dominant-baseline="middle">' + d.short + '</text>'
      );
    }).join('');

    /* The accessible name carries the numbers, because the shape itself is not
       readable by anyone using a screen reader. */
    const summary = DIMENSIONS.map(function (d) {
      return d.label + ' ' + data.scores[d.id] + ' out of 100';
    }).join(', ');

    return (
      '<div class="kv4-rep__radar" data-rep-radar>' +
      /* Wider than the geometry so the side labels live inside the box and
         scale down with it. Relying on overflow instead would clip them in the
         narrow mobile column. */
      '<svg viewBox="-30 -4 246 208" width="246" height="208" role="img" aria-label="Your profile: ' + summary + '">' +
      '<g>' + rings + spokes + '</g>' +
      labels +
      '<g class="kv4-rep__shape-g" data-rep-shape>' +
      '<polygon class="kv4-rep__shape" points="' + shape + '"/>' +
      dots +
      '</g>' +
      '</svg>' +
      '</div>'
    );
  }

  /* The readable version of the same five numbers. The radar shows shape, this
     shows detail, and people who skim read this one. */
  function bars(data, ctx) {
    const icon = ctx.icon;
    return (
      '<ul class="kv4-rep__bars">' +
      DIMENSIONS.map(function (d, i) {
        const value = data.scores[d.id];
        const read = READS[d.id][band(value)];
        return (
          '<li class="kv4-rep__bar" style="--i:' + i + '">' +
          '<div class="kv4-rep__bar-head">' +
          '<span class="kv4-rep__bar-icon">' + icon(d.icon) + '</span>' +
          '<span class="kv4-rep__bar-label">' + d.label + '</span>' +
          '<span class="kv4-rep__bar-value">' + value + '</span>' +
          '</div>' +
          '<div class="kv4-rep__track"><span class="kv4-rep__fill" style="--v:' + value + '%" data-rep-fill></span></div>' +
          '<p class="kv4-rep__bar-read">' + read + '</p>' +
          '</li>'
        );
      }).join('') +
      '</ul>'
    );
  }

  function chips(list) {
    return (
      '<ul class="kv4-rep__chips">' +
      list
        .map(function (m, i) {
          return '<li class="kv4-rep__chip" style="--i:' + i + '">' + m.label + '</li>';
        })
        .join('') +
      '</ul>'
    );
  }

  /* ---------- Public: render ----------
     ctx supplies everything customer-facing that is not report copy: the
     locale strings, the icon glyphs and the merchant's product data. */
  function render(data, ctx) {
    const S = ctx.strings;
    const icon = ctx.icon;
    const p = data.profile;
    const lead = DIMENSIONS.filter(function (d) {
      return d.id === p.lead;
    })[0];

    const fill = function (text, map) {
      let out = text;
      Object.keys(map).forEach(function (key) {
        out = out.split('{{ ' + key + ' }}').join(map[key]);
      });
      return out;
    };

    /* `kind` is 'num' or 'text'. A dimension name needs to wrap in a third of
       the column, so it cannot take the display size the figures get. */
    const stat = function (value, label, kind) {
      return (
        '<div class="kv4-rep__stat">' +
        '<span class="kv4-rep__stat-v kv4-rep__stat-v--' + (kind || 'num') + '">' + value + '</span>' +
        '<span class="kv4-rep__stat-l">' + label + '</span>' +
        '</div>'
      );
    };

    const cluster = function (title, list) {
      if (!list.length) return '';
      return '<div class="kv4-rep__cluster"><h4>' + title + '</h4>' + chips(list) + '</div>';
    };

    const product = function (key, isLead) {
      const item = ctx.products[key];
      if (!item) return '';
      return (
        '<article class="kv4-quiz__card' + (isLead ? ' kv4-quiz__card--lead' : '') + '">' +
        '<img src="' + item.image + '" alt="" width="640" height="640">' +
        '<div class="kv4-quiz__card-body">' +
        '<p class="kv4-quiz__also">' + (isLead ? S.repPick : S.also) + '</p>' +
        '<h3>' + item.title + '</h3>' +
        '<p>' + item.why + '</p>' +
        '<a class="kv-btn kv4-quiz__cta kv4-quiz__cta--inline" href="' + item.url + '">' + S.shop + '</a>' +
        '</div>' +
        '</article>'
      );
    };

    const leadName = ctx.products[p.primary] ? ctx.products[p.primary].title : '';

    /* Framed as how to run the ritual, not as what it will do for you. Every
       step is an action the visitor takes, which keeps it out of claim
       territory and makes it genuinely followable. */
    const steps = [
      { when: S.repPlan1When, what: fill(S.repPlan1, { product: leadName }) },
      { when: S.repPlan2When, what: S.repPlan2 },
      { when: S.repPlan3When, what: S.repPlan3 },
      { when: S.repPlan4When, what: S.repPlan4 },
    ];

    return (
      '<div class="kv4-quiz__screen kv4-quiz__screen--report kv4-rep">' +
      /* Header */
      '<header class="kv4-rep__head">' +
      '<p class="kv4-quiz__eyebrow">' + S.repEyebrow + '</p>' +
      '<h2 class="kv4-quiz__title" tabindex="-1">' + p.name + '</h2>' +
      '<p class="kv4-rep__tagline">' + p.tagline + '</p>' +
      '</header>' +
      /* Signature + numbers */
      '<section class="kv4-rep__block">' +
      radar(data) +
      '<div class="kv4-rep__stats">' +
      stat(S.percent.split('{{ value }}').join(String(data.fit)), S.repMatch, 'num') +
      stat(String(data.markers.length), S.repMarkers, 'num') +
      stat(lead ? lead.label : '', S.repFocus, 'text') +
      '</div>' +
      '</section>' +
      /* Reading */
      '<section class="kv4-rep__block">' +
      '<h3 class="kv4-rep__h">' + S.repReading + '</h3>' +
      p.reading
        .map(function (para) {
          return '<p class="kv4-rep__p">' + para + '</p>';
        })
        .join('') +
      '<div class="kv4-rep__notes">' +
      '<div class="kv4-rep__note"><span class="kv4-rep__note-k">' + S.repStrength + '</span><p>' + p.strength + '</p></div>' +
      '<div class="kv4-rep__note kv4-rep__note--focus"><span class="kv4-rep__note-k">' + S.repOpportunity + '</span><p>' + p.focus + '</p></div>' +
      '</div>' +
      '</section>' +
      /* The five numbers */
      '<section class="kv4-rep__block">' +
      '<h3 class="kv4-rep__h">' + S.repScores + '</h3>' +
      '<p class="kv4-rep__sub">' + S.repScoresSub + '</p>' +
      bars(data, ctx) +
      '</section>' +
      /* Markers, grouped by half of the day */
      (data.markers.length
        ? '<section class="kv4-rep__block">' +
          '<h3 class="kv4-rep__h">' + S.repMarkersTitle + '</h3>' +
          '<p class="kv4-rep__sub">' + S.repMarkersSub + '</p>' +
          cluster(S.repEvening, data.evening) +
          cluster(S.repDaytime, data.daytime) +
          '</section>'
        : '') +
      /* Plan */
      '<section class="kv4-rep__block">' +
      '<h3 class="kv4-rep__h">' + S.repPlanTitle + '</h3>' +
      '<p class="kv4-rep__sub">' + S.repPlanSub + '</p>' +
      '<ol class="kv4-rep__plan">' +
      steps
        .map(function (s, i) {
          return (
            '<li class="kv4-rep__step" style="--i:' + i + '">' +
            '<span class="kv4-rep__step-n">' + (i + 1) + '</span>' +
            '<div><span class="kv4-rep__step-when">' + s.when + '</span><p>' + s.what + '</p></div>' +
            '</li>'
          );
        })
        .join('') +
      '</ol>' +
      '</section>' +
      /* Offer */
      '<section class="kv4-rep__block kv4-rep__offer">' +
      '<h3 class="kv4-rep__h">' + S.repOfferTitle + '</h3>' +
      '<p class="kv4-rep__sub">' + fill(S.repOfferSub, { profile: p.name }) + '</p>' +
      product(p.primary, true) +
      (p.secondary ? product(p.secondary, false) : '') +
      '</section>' +
      /* Close */
      '<footer class="kv4-rep__foot">' +
      '<p class="kv4-quiz__fine">' + S.repMethod + '</p>' +
      '<p class="kv4-quiz__fine">' + S.disclaimer + '</p>' +
      '<a class="kv-btn kv-btn--light kv4-quiz__home-btn" href="' + ctx.homeUrl + '">' + icon('house') + '<span>' + S.backHome + '</span></a>' +
      '<button type="button" class="kv4-quiz__restart" data-quiz-restart>' + S.restart + '</button>' +
      '</footer>' +
      '</div>'
    );
  }

  /* Bars grow when they scroll into view. The radar sits at the top of the
     report, so it is drawn on the next frame: observing an SVG <g> on iOS
     never intersects, which left the shape at opacity 0 on phones. */
  function animate(root, reduce) {
    const radar = root.querySelector('[data-rep-radar]');
    const shape = root.querySelector('[data-rep-shape]');
    const fills = root.querySelectorAll('[data-rep-fill]');
    const showRadar = function () {
      if (radar) radar.classList.add('is-in');
      if (shape) shape.classList.add('is-in');
    };

    if (reduce || !('IntersectionObserver' in window)) {
      showRadar();
      fills.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(showRadar);
    });
    window.setTimeout(showRadar, 80);

    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );
    fills.forEach(function (el) {
      io.observe(el);
    });
  }

  /* Properties for Klaviyo. This is the capture path that works for everyone:
     Shopify only writes contact[tags] when it creates the customer, so a past
     purchaser or existing subscriber who takes the quiz would otherwise arrive
     with no profile attached to them at all.

     Title Case keys because that is how they render in the Klaviyo segment
     builder. Scores are sent as numbers so they can be used in numeric
     conditions rather than only string matches. */
  function profileProps(data, answers) {
    const a = answers || {};
    const props = {
      'Quiz Profile': data.profile.name,
      'Quiz Profile Id': data.profile.id,
      'Quiz Profile Tagline': data.profile.tagline,
      'Quiz Match': data.plan.primary,
      'Quiz Also': data.plan.secondary || 'none',
      'Quiz Fit': data.fit,
      'Quiz Primary Focus': data.profile.lead,
      'Quiz Markers': data.markers.map(function (m) {
        return m.label;
      }),
      'Quiz Marker Count': data.markers.length,
      'Quiz Evening Markers': data.evening.length,
      'Quiz Daytime Markers': data.daytime.length,
      'Quiz Completed At': new Date().toISOString(),
    };
    DIMENSIONS.forEach(function (d) {
      props['Quiz Score ' + d.label] = data.scores[d.id];
    });
    /* The raw answers too, so a segment can be built on a single response
       without having to reverse it out of the scores. */
    Object.keys(a).forEach(function (key) {
      const title = key
        .split('_')
        .map(function (w) {
          return w.charAt(0).toUpperCase() + w.slice(1);
        })
        .join(' ');
      props['Quiz Answer ' + title] = Array.isArray(a[key]) ? a[key] : [a[key]];
    });
    return props;
  }

  /* Tags for Shopify. Profile and score bands travel with the answers so the
     list is segmentable by who someone is, not just what they clicked. */
  function tags(data) {
    const out = ['quiz', 'newsletter', 'quiz-profile-' + data.profile.id.split('_').join('-')];
    out.push('quiz-match-' + data.plan.primary);
    if (data.plan.secondary) out.push('quiz-also-' + data.plan.secondary);
    DIMENSIONS.forEach(function (d) {
      out.push('quiz-' + d.id + '-' + band(data.scores[d.id]));
    });
    return out;
  }

  return {
    build: build,
    render: render,
    animate: animate,
    tags: tags,
    profileProps: profileProps,
    profiles: PROFILES,
    dimensions: DIMENSIONS,
  };
})();
